/**
 * Экран «Проживания» рабочего места менеджера — по согласованному дизайну
 * manager v3 §5.1 (`/manager/stays`, lovart-hotels-design-manager-v3-2026-06-06.html).
 *
 * Layout по макету: header «Проживания · N» + «+ Заселение» (canManage) ·
 * фильтр-чипы со счётчиками (Живут / Заезд скоро / Выехали / Все) + поиск по
 * имени/номеру · десктоп — таблица (Гость, Номер, Заезд, Выезд, Ночей, Сумма,
 * Статус, →) · мобайл 375 — карточки (гость+статус, № · ночи · сумма, даты).
 * Клик по строке/карточке → StayDetail.
 *
 * По умолчанию активна вкладка «Живут» (in_house).
 *
 * Источник списка — серверный GET /v1/stays?status=&sort=&order=&page=&
 * page_size=&q= (фильтр/сортировка/пагинация в БД). Большой отель ~10 000
 * проживаний/мес не тянется в браузер: фильтр по статусу + сортировка по
 * номеру/заезду/выезду + OFFSET-пагинация отрабатывают на бэке. Карточка —
 * GET /v1/stays/{id}; действия жизненного цикла — POST confirm/check-in/
 * check-out/cancel.
 *
 * RBAC: список + действия — staff/management (гость 403 на бэке).
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, Skeleton } from '@hsc/ui';
import type { StayListItem, StayListSort, StayListStatus, StayState } from '@hsc/types';
import {
  STAY_STATE_LABEL,
  STAY_STATE_TONE,
  formatDate,
  formatMoney,
} from '../../lib/fleet-format.js';
import { useStaysList } from './useStays.js';
import { StayDetail } from './StayDetail.js';

/** Категория-фильтр проживаний (по дизайну manager v3 §5.1). Дефолт «Живут». */
type Tab = StayListStatus;

const TABS: ReadonlyArray<{ key: Tab; label: string; tone: string }> = [
  { key: 'in_house', label: 'Живут', tone: 'ok' },
  { key: 'arrivals', label: 'Заезд скоро', tone: 'busy' },
  { key: 'checked_out', label: 'Выехали', tone: 'muted' },
  { key: 'all', label: 'Все', tone: '' },
];

const PAGE_SIZE = 20;

/** Селект строки, передаваемый в StayDetail (минимальная карточка-хедер). */
interface SelectedStay {
  stayId: string;
  roomNumber: string;
  guestName: string;
}

/** Число ночей между заездом и выездом (минимум 1). */
function nightsBetween(from: string, to: string): number {
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 1;
  const ms = b.getTime() - a.getTime();
  return Math.max(1, Math.round(ms / 86_400_000));
}

/** «N ночей» с русским склонением. */
function nightsLabel(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = 'ночей';
  if (mod10 === 1 && mod100 !== 11) word = 'ночь';
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) word = 'ночи';
  return `${n} ${word}`;
}

export interface StaysPageProps {
  canManage?: boolean;
}

export function StaysPage({ canManage = false }: StaysPageProps): JSX.Element {
  const [tab, setTab] = useState<Tab>('in_house');
  const [sort, setSort] = useState<StayListSort>('check_in');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<SelectedStay | null>(null);

  // Дебаунс поиска, чтобы не слать запрос на каждый символ.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Смена фильтра/сортировки/поиска → страница в 1.
  useEffect(() => {
    setPage(1);
  }, [tab, sort, order, debouncedQuery]);

  const { data, isLoading, isError, isFetching } = useStaysList({
    status: tab,
    sort,
    order,
    page,
    page_size: PAGE_SIZE,
    q: debouncedQuery,
  });

  const rows: StayListItem[] = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeTo = Math.min(page * PAGE_SIZE, total);

  /** Клик по сортируемому заголовку: тот же столбец → инверсия, иначе asc. */
  const onSort = (key: StayListSort): void => {
    if (sort === key) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setOrder('asc');
    }
  };

  const sortArrow = (key: StayListSort): string => {
    if (sort !== key) return '';
    return order === 'asc' ? '↑' : '↓';
  };

  const badge = (status: string): JSX.Element => (
    <span className={`fleet-badge fleet-badge--${STAY_STATE_TONE[status as StayState] ?? 'muted'}`}>
      {STAY_STATE_LABEL[status as StayState] ?? status}
    </span>
  );

  const sortableTh = (key: StayListSort, label: string): JSX.Element => (
    <th className="stays-sort-th">
      <button
        type="button"
        className={`stays-sort-btn${sort === key ? ' is-active' : ''}`}
        onClick={() => onSort(key)}
        data-testid={`stays-sort-${key}`}
        aria-label={`Сортировать по «${label}»`}
      >
        {label}
        <span className="stays-sort-arrow" aria-hidden="true">
          {sortArrow(key)}
        </span>
      </button>
    </th>
  );

  // Карточка проживания открывается ОТДЕЛЬНЫМ ЭКРАНОМ — список не виден,
  // «Назад» возвращает к списку ([[feedback_every_form_is_a_separate_screen]]).
  if (selected !== null) {
    return (
      <StayDetail
        stayId={selected.stayId}
        canManage={canManage}
        roomNumber={selected.roomNumber}
        guestName={selected.guestName}
        onClose={() => setSelected(null)}
      />
    );
  }

  return (
    <section data-testid="stays-page" className="stays-page">
      <div className="stays-head">
        <h1 className="hsc-page__title">
          Проживания{!isLoading && !isError && data ? ` · ${total}` : ''}
        </h1>
        {canManage && (
          <button type="button" className="stays-cta" data-testid="stays-checkin-cta" disabled>
            + Заселение
          </button>
        )}
      </div>

      <div className="fleet-statuses" role="tablist" data-testid="stays-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`fleet-chip${t.tone ? ` fleet-chip--${t.tone}` : ''}${tab === t.key ? ' is-active' : ''}`}
            onClick={() => setTab(t.key)}
            data-testid={`stays-tab-${t.key}`}
          >
            {t.label}
            {tab === t.key ? ` · ${total}` : ''}
          </button>
        ))}
      </div>

      <div className="stays-search">
        <input
          type="search"
          className="ta-input stays-search__input"
          placeholder="Поиск по имени или номеру…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Поиск проживаний"
          data-testid="stays-search"
        />
      </div>

      {isLoading ? (
        <div className="fleet-list" data-testid="stays-skeleton">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} height={56} radius={14} />
          ))}
        </div>
      ) : isError || !data ? (
        <Card>
          <p role="alert">
            Что-то не складывается. Попробуйте обновить страницу — если повторится, напишите нам.
          </p>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <p data-testid="stays-empty" className="stays-empty">
            {debouncedQuery !== ''
              ? 'По вашему запросу ничего не нашлось. Попробуйте другое имя или номер.'
              : 'Здесь пока пусто. Когда появится проживание этой категории, его карточка появится тут.'}
          </p>
        </Card>
      ) : (
        <>
          {/* Десктоп/планшет — таблица по дизайну §5.1 */}
          <div className="stays-table-wrap" data-testid="stays-table" aria-busy={isFetching}>
            <table className="stays-table">
              <thead>
                <tr>
                  <th>Гость</th>
                  {sortableTh('room', 'Номер')}
                  {sortableTh('check_in', 'Заезд')}
                  {sortableTh('check_out', 'Выезд')}
                  <th className="stays-col-nights">Ночей</th>
                  <th className="stays-col-sum">Сумма</th>
                  <th>Статус</th>
                  <th aria-hidden="true"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const nights = nightsBetween(r.check_in, r.check_out);
                  return (
                    <tr
                      key={r.stay_id}
                      className="stays-table__row"
                      onClick={() =>
                        setSelected({
                          stayId: r.stay_id,
                          roomNumber: r.room_number,
                          guestName: r.guest_name,
                        })
                      }
                      tabIndex={0}
                      role="button"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelected({
                            stayId: r.stay_id,
                            roomNumber: r.room_number,
                            guestName: r.guest_name,
                          });
                        }
                      }}
                      data-testid={`stay-row-${r.stay_id}`}
                    >
                      <td>
                        <strong>{r.guest_name.trim() !== '' ? r.guest_name : 'Гость без имени'}</strong>
                      </td>
                      <td>№ {r.room_number}</td>
                      <td>{formatDate(r.check_in)}</td>
                      <td>{formatDate(r.check_out)}</td>
                      <td className="stays-col-nights">{nights}</td>
                      <td className="stays-col-sum">
                        {r.total_kopecks !== undefined && r.total_kopecks !== null
                          ? formatMoney(r.total_kopecks, r.currency)
                          : '—'}
                      </td>
                      <td>{badge(r.status)}</td>
                      <td className="stays-table__chev" aria-hidden="true">
                        →
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Мобайл 375 — карточки по дизайну §5.1 */}
          <ul className="stays-cards" data-testid="stays-list">
            {rows.map((r) => {
              const nights = nightsBetween(r.check_in, r.check_out);
              return (
                <li key={r.stay_id}>
                  <button
                    type="button"
                    className="stays-card"
                    onClick={() =>
                      setSelected({
                        stayId: r.stay_id,
                        roomNumber: r.room_number,
                        guestName: r.guest_name,
                      })
                    }
                    data-testid={`stay-card-${r.stay_id}`}
                  >
                    <div className="stays-card__top">
                      <strong className="stays-card__name">
                        {r.guest_name.trim() !== '' ? r.guest_name : 'Гость без имени'}
                      </strong>
                      {badge(r.status)}
                    </div>
                    <div className="stays-card__mid">
                      <span className="stays-card__room">
                        № {r.room_number} · {nightsLabel(nights)}
                      </span>
                      <strong className="stays-card__sum">
                        {r.total_kopecks !== undefined && r.total_kopecks !== null
                          ? formatMoney(r.total_kopecks, r.currency)
                          : ''}
                      </strong>
                    </div>
                    <div className="stays-card__dates">
                      {formatDate(r.check_in)} → {formatDate(r.check_out)}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Пагинация (серверная, OFFSET) */}
          <div className="stays-pagination" data-testid="stays-pagination">
            <span className="stays-pagination__info" data-testid="stays-range">
              {rangeFrom}–{rangeTo} из {total}
            </span>
            <div className="stays-pagination__controls">
              <button
                type="button"
                className="stays-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isFetching}
                data-testid="stays-prev"
                aria-label="Предыдущая страница"
              >
                ←
              </button>
              <span className="stays-page-current" data-testid="stays-page-current">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                className="stays-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || isFetching}
                data-testid="stays-next"
                aria-label="Следующая страница"
              >
                →
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
